import { useEffect, useState } from 'react';
import {
  toPitchPositions,
  type PitchPositions,
  type TacticsLayout,
} from '@4cm/core/tactics';
import { Pitch } from '../Pitch';
import {
  loadTacticsLayout,
  onTacticsLayoutChanged,
} from '../../lib/tactics3dStore';

export interface DrillTacticsThumbnailProps {
  /** Drill id used to derive the `tactics-drill-${drillId}` persistKey. */
  drillId: string;
  /** Caption rendered as the SVG's faint mono label — usually the drill name. */
  caption?: string;
  /** Pixel size of the rendered thumbnail; defaults are tuned for the chip rail. */
  width?: number;
  height?: number;
}

const PLACEHOLDER_VIEWPORT = { width: 360, height: 200 };

/**
 * Centred 4-3-3 ghost layout shown when a drill has no saved tactical
 * layout. The positions are derived from the same projection the
 * interactive board uses (via `toPitchPositions`) so the placeholder
 * reads as "the board hasn't been opened yet" rather than a different
 * primitive entirely.
 *
 * Picked once at module load so every blank thumbnail renders the same
 * ghost — important visual signal for coaches scanning the timeline.
 */
const PLACEHOLDER: PitchPositions = (() => {
  // 4-3-3 home spots in world metres. Hardcoded (not imported from
  // formationProjection) to keep this component decoupled from the
  // interactive board's internals — the value is stable.
  const fakeLayout: TacticsLayout = {
    v: 3,
    homeFormation: '4-3-3',
    awayFormation: '4-3-3',
    homeSpots: [
      { role: 'GK', x: 0, y: 0, z: 47 },
      { role: 'LB', x: -22, y: 0, z: 28 },
      { role: 'LCB', x: -8, y: 0, z: 30 },
      { role: 'RCB', x: 8, y: 0, z: 30 },
      { role: 'RB', x: 22, y: 0, z: 28 },
      { role: 'LCM', x: -16, y: 0, z: 8 },
      { role: 'CDM', x: 0, y: 0, z: 12 },
      { role: 'RCM', x: 16, y: 0, z: 8 },
      { role: 'LW', x: -24, y: 0, z: -14 },
      { role: 'ST', x: 0, y: 0, z: -22 },
      { role: 'RW', x: 24, y: 0, z: -14 },
    ],
    awaySpots: [],
    arrows: [],
    saved_at: new Date(0).toISOString(),
  };
  return toPitchPositions(fakeLayout, PLACEHOLDER_VIEWPORT);
})();

/**
 * Static thumbnail render of a drill's tactics layout for the timeline
 * card. Read-only — no drag, no edit, no HUD. Falls back to a faint
 * centred 4-3-3 ghost when no layout is saved for this drill yet.
 *
 * Subscribes to `onTacticsLayoutChanged` so the thumbnail refreshes
 * after the inline TacticsBoard2D saves a new layout for the selected
 * drill, without the parent having to re-render.
 */
export function DrillTacticsThumbnail({
  drillId,
  caption,
  width = 360,
  height = 200,
}: DrillTacticsThumbnailProps) {
  const persistKey = `tactics-drill-${drillId}`;
  const [layout, setLayout] = useState<TacticsLayout | null>(() =>
    loadTacticsLayout(persistKey),
  );

  // Refresh when the inline board saves under this key. Also rehydrate
  // when the drillId itself changes — drag-reorder keeps the same name
  // (so drillId is stable) but a rename produces a new persistKey, and
  // we want the thumbnail to reflect the new slot's saved state.
  useEffect(() => {
    setLayout(loadTacticsLayout(persistKey));
    return onTacticsLayoutChanged(persistKey, () => {
      setLayout(loadTacticsLayout(persistKey));
    });
  }, [persistKey]);

  const positions: PitchPositions =
    layout && layout.homeSpots.length > 0
      ? toPitchPositions(layout, { width, height })
      : PLACEHOLDER;

  const isPlaceholder = !layout || layout.homeSpots.length === 0;

  return (
    <div
      className="session-canvas__chip-thumb"
      data-testid={`drill-thumb-${drillId}`}
      data-empty={isPlaceholder || undefined}
      aria-hidden="true"
    >
      <Pitch
        caption={caption ?? ''}
        positions={positions}
        width={width}
        height={height}
      />
    </div>
  );
}
